/**
 * Interval durations in milliseconds for time-averaging.
 */
const INTERVAL_MS = {
  '1m':  60 * 1000,
  '5m':  5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h':  60 * 60 * 1000,
  '3h':  3 * 60 * 60 * 1000,
  '6h':  6 * 60 * 60 * 1000,
  '1d':  24 * 60 * 60 * 1000,
};

/**
 * Compute time-averaged data for each variable/location/device combination.
 *
 * @param {Array} rows - Raw data rows with { ts, node, name, value, device, sensorId, unit, meta, ... }
 * @param {string} interval - One of '1m','5m','15m','30m','1h','6h','1d'
 * @returns {Array} Averaged rows with the same shape, plus `count` and `std` fields.
 */
export function computeAverages(rows, interval) {
  const ms = INTERVAL_MS[interval];
  if (!ms) return rows; // Unknown interval, return raw

  // Group by composite key (variable + location + device) and time bucket
  const buckets = {};

  for (const row of rows) {
    const t = new Date(row.ts).getTime();
    const bucketStart = Math.floor(t / ms) * ms;
    const key = `${row.name}||${row.node}||${row.sensorId || ''}||${bucketStart}`;

    if (!buckets[key]) {
      buckets[key] = {
        bucketStart,
        values: [],
        // Keep a representative row for metadata
        representative: row,
      };
    }
    const numVal = parseFloat(row.value);
    if (!isNaN(numVal)) {
      buckets[key].values.push(numVal);
    }
  }

  // Format a Date to match the API's timezone-adjusted format (YYYY-MM-DDTHH:MM:SS, no Z)
  const pad = (n) => String(n).padStart(2, '0');
  const formatLocalISO = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

  // Build averaged rows
  const result = [];
  for (const key of Object.keys(buckets)) {
    const { bucketStart, values, representative } = buckets[key];
    if (values.length === 0) continue;

    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.length > 1
      ? values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (values.length - 1)
      : 0;

    result.push({
      ...representative,
      ts: formatLocalISO(new Date(bucketStart)),
      value: Math.round(mean * 10000) / 10000,
      count: values.length,
      min: Math.round(Math.min(...values) * 10000) / 10000,
      max: Math.round(Math.max(...values) * 10000) / 10000,
      std: Math.round(Math.sqrt(variance) * 10000) / 10000,
    });
  }

  // Sort by timestamp descending (same as raw data)
  result.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  return result;
}

export function seededRandom(seed) { 
  let x = Math.sin(seed) * 10000; 
  return x - Math.floor(x); 
}

export function buildSevenDaySeries(seed = 1) {
  const hours = 168, out = [];
  for (let i = 0; i < hours; i++) {
    const hod = i % 24, isDay = hod >= 7 && hod <= 19;
    // eslint-disable-next-line no-unused-vars
    const baseProb = isDay ? 0.85 : 0.3;
    const randomFactor = seededRandom(seed + i);
    
    // Create varying probabilities to get different densities
    let availability;
    if (randomFactor < 0.03) {
      availability = 0; // No data (3% chance)
    } else if (randomFactor < 0.08) {
      availability = 0.3; // Low density (5% chance)
    } else if (randomFactor < 0.15) {
      availability = 0.5; // Medium density (7% chance)
    } else if (randomFactor < 0.25) {
      availability = 0.7; // Good density (10% chance)
    } else {
      availability = 1.0; // Full data (75% chance)
    }
    
    // Adjust based on time of day
    if (!isDay && availability > 0) {
      availability = Math.max(0.3, availability * 0.7);
    }
    
    out.push(availability);
  }
  return out;
}

export function generateRandomRows({ startISO, endISO, nodes, names }) {
  const start = +new Date(startISO);
  const end = +new Date(endISO);
  const ms = Math.max(1000, end - start);
  const rows = [];
  
  // Device ID mappings - use actual device IDs
  const deviceIdPool = ['MNLA4O10C', 'MNLA4O10A', 'MNLA4O10B', 'SX61NA09', 'SX61NA0U', 'SX61NA0I'];
  
  // Meta data mappings
  const sapSpecies = {
    'SX61NA09': "Maple",
    'SX61NA0U': "Elm", 
    'SX61NA0I': "Tree of Heaven",
  };
  
  const sapDBH = {
    'SX61NA09': "45 cm",
    'SX61NA0U': "38 cm",
    'SX61NA0I': "52 cm",
  };
  
  const mfrSites = {
    'MNLA4O10C': "Champlain",
    'MNLA4O10A': "Delta",
    'MNLA4O10B': "Langley",
  };
  
  nodes.forEach((node) => {
    names.forEach((name, idx) => {
      const seed = Array.from(`${node}:${name}`).reduce((a, ch) => a + ch.charCodeAt(0), 0);
      const N = 90 + (seed % 40);
      
      // Determine if sap flow or MFR based on variable name
      const isSapFlow = name.includes('uncorrected') || name.includes('sap');
      
      // Pick an appropriate device ID based on type
      const deviceIdIndex = (seed % (isSapFlow ? 3 : 3)) + (isSapFlow ? 3 : 0);
      const sensorId = deviceIdPool[deviceIdIndex];
      
      const species = isSapFlow ? sapSpecies[sensorId] : null;
      const dbh = isSapFlow ? sapDBH[sensorId] : null;
      const site = !isSapFlow ? mfrSites[sensorId] : null;
      
      for (let i = 0; i < N; i++) {
        const t = start + Math.floor((i / (N - 1)) * ms);
        const diurnal = 22 + 6 * Math.sin((i / (N - 1)) * Math.PI * 4 + (idx * Math.PI) / 8);
        const noise = (seededRandom(seed + i) - 0.5) * 3.0;
        const drift = (i / (N - 1)) * (seed % 5);
        const val = Math.round((diurnal + noise + drift) * 100) / 100;
        
        rows.push({
          ts: new Date(t).toISOString(),
          node,
          name,
          value: name.includes("battery") ? Math.round((4 + (noise / 10)) * 1000) / 1000 : val,
          device: (isSapFlow ? "Sap Flow" : "MFR") + " device",
          species,
          dbh,
          site,
          sensorId,
          devEui: isSapFlow ? 
            (sensorId === 'SX61NA09' ? '016509237ec092ee' : 
             sensorId === 'SX61NA0U' ? '44bf2806b70c2311' : '56de24386bd33383') :
            (sensorId === 'MNLA4O10C' ? '2cc17e2d7cb601a2' :
             sensorId === 'MNLA4O10A' ? '379afb6f83da6653' : 'eca11baaeac43cba')
        });
      }
    });
  });
  
  return rows;
}